import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type { SingleValue } from "react-select";

import {
	faChevronDown,
	faChevronUp,
	faClock as faClockLight,
	faStar as faStarLight,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faScissors } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faClock as faClockSolid, faStar as faStarSolid } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { motion } from "framer-motion";
import { throttle } from "lodash";
import { MediaChromeButton, type MediaPreviewThumbnail as MediaPreviewThumbnailElement } from "media-chrome";
import {
	MediaCaptionsButton,
	MediaControlBar,
	MediaController,
	MediaFullscreenButton,
	MediaMuteButton,
	MediaPlayButton,
	MediaPreviewThumbnail,
	MediaTimeDisplay,
	MediaVolumeRange,
} from "media-chrome/dist/react";
import useResizeObserver from "use-resize-observer";

import { BillingContext } from "~/account/Billing/BillingContext";
import { AppContext } from "~/common";
import { zindexRadixPopper } from "~/css/zindexes";
import { useForwardedRef, useMedia } from "~/lib/hooks";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { MIN_VIDEO_DURATION_FOR_CLIPPING } from "~/meetingRecorder/ClipTrimmer";
import PlaybackRateSelector from "~/meetingRecorder/Video/PlaybackRateSelector";
import { Timeline } from "~/meetingRecorder/Video/Timeline";
import { useLibraryNavigationFeatureFlagOn } from "~/nav/featureFlag";
import { addOrRemoveRecordingOrClipFromFavourites } from "~/recordings/LibraryPostActions";
import { Button, Tooltip } from "~/ui";
import Menu from "~/ui/menu";
import Selector, { type SelectorOption } from "~/ui/selectors/Selector";
import { MenuWithForcedDarkTheme } from "~/ui/selectors/SelectorUtils";

import {
	addTextTrackCuesToChapters,
	type BulletPoint,
	formatDate,
	getThumbnailImageCoords,
	useIsNarrowViewport,
	useIsPublic,
} from "../utils";
import CenteredControls, { type CenteredControlsRenderProps } from "./CenteredControls";
import { ReactComponent as CaptionsOffIcon } from "./icons/captions-off.svg";
import { ReactComponent as CaptionsOnIcon } from "./icons/captions-on.svg";
import { ReactComponent as FullscreenEnterIcon } from "./icons/fullscreen-enter.svg";
import { ReactComponent as FullscreenExitIcon } from "./icons/fullscreen-exit.svg";
import { ReactComponent as MiniplayerIcon } from "./icons/miniplayer.svg";
import { ReactComponent as NextChapterIcon } from "./icons/next-chapter.svg";
import { ReactComponent as PauseIcon } from "./icons/pause.svg";
import { ReactComponent as PlayIcon } from "./icons/play.svg";
import { ReactComponent as VolumeHighIcon } from "./icons/volume-high.svg";
import { ReactComponent as VolumeMediumIcon } from "./icons/volume-medium.svg";
import { ReactComponent as VolumeOffIcon } from "./icons/volume-off.svg";

import type { Timeline_meetingKeyEvents$key } from "~/__generated__/Timeline_meetingKeyEvents.graphql";
import type { Video_meetingRecording$key } from "~/__generated__/Video_meetingRecording.graphql";

import styles from "./Video.module.css";

// NOTE: Keep in sync with the value in apps/meeting_recorder/api/clips.py
const MAX_CLIPS_PER_RECORDING = 10;

type CustomElementProps<T extends Element> = Omit<
	React.DetailedHTMLProps<React.HTMLAttributes<T>, T>,
	"className" | "htmlFor"
> & { class?: string; for?: string };

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace JSX {
		interface IntrinsicElements {
			"fellow-video-player-button": CustomElementProps<FellowVideoPlayerButtonElement> & {
				disabled?: boolean;
				preventClick?: boolean;
			};
		}
	}
}

interface VideoProps extends React.HTMLProps<HTMLVideoElement> {
	isMiniPlayerActive?: boolean;
	toggleMiniPlayer?(): void;
	thumbnailTrack: TextTrack | null;
	inClippingMode: boolean;
	openClipTrimmer?: () => void;
	numClips: number;
	isClip: boolean;
	parts?: { value: string; label: string }[];
	setSelectedRecordingId?: (recordingId: string) => void;
	hideClipButton?: boolean;
	allowRecapDownload?: boolean;
	meetingRecordingKey: Video_meetingRecording$key;
	isCollapsed: boolean;
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const slotTemplate = document.createElement("template");
slotTemplate.innerHTML = `
  <slot name="icon"></slot>
`;

class FellowVideoPlayerButtonElement extends MediaChromeButton {
	constructor(options = {}) {
		super({ slotTemplate, ...options });
	}

	static get observedAttributes() {
		return [...super.observedAttributes];
	}

	_clickHandlers: ((event: Event) => void)[] = [];
	addClickListener(listener: (event: Event) => void): void {
		this._clickHandlers.push(listener);
	}

	removeClickListener(listener: (event: Event) => void): void {
		this._clickHandlers = this._clickHandlers.filter(handler => handler !== listener);
	}

	handleClick(event: Event) {
		if (this.getAttribute("disabled") !== null) return;

		for (const listener of this._clickHandlers) {
			try {
				listener(event);
			} catch (e) {
				Sentry.captureException(e);
			}
		}
	}
}

if (!globalThis.customElements.get("fellow-video-player-button")) {
	globalThis.customElements.define("fellow-video-player-button", FellowVideoPlayerButtonElement);
}

export const FellowVideoPlayerButton = React.forwardRef<
	FellowVideoPlayerButtonElement,
	Omit<
		React.DetailedHTMLProps<React.HTMLAttributes<FellowVideoPlayerButtonElement>, FellowVideoPlayerButtonElement>,
		"onClick"
	> & {
		onClick?: (event: Event) => void;
		disabled?: boolean;
	}
>(function FellowVideoPlayerButton({ onClick, children, className, disabled, ...props }, forwardedRef) {
	const ref = useForwardedRef(forwardedRef);

	React.useEffect(() => {
		if (!onClick) return;

		const el = ref.current;
		el?.addClickListener?.(onClick);
		return () => {
			el?.removeClickListener?.(onClick);
		};
	}, [ref, onClick]);

	return (
		<fellow-video-player-button ref={ref} disabled={disabled || undefined} class={className} {...props}>
			{children}
		</fellow-video-player-button>
	);
});

interface ControlTooltipProps {
	children: React.ComponentProps<typeof Tooltip>["children"];
	content: React.ReactNode;
	disabled?: boolean;
	onMount?: () => void;
	className?: string;
	placement?: React.ComponentProps<typeof Tooltip>["placement"];
	offset?: React.ComponentProps<typeof Tooltip>["offset"];
	triggerTarget?: HTMLElement;
	visible?: boolean;
}

// Tooltips should be 8px above the control
// Can't use as const because then it will be readonly and Tippy's props expect
// a mutable pair of numbers :|
const TOOLTIP_OFFSET: [number, number] = [0, 16];

export const ControlTooltip = React.forwardRef<Element, ControlTooltipProps>(function ControlTooltip(
	{
		children,
		content,
		disabled,
		onMount,
		className,
		triggerTarget,
		visible,
		placement = "top",
		offset = TOOLTIP_OFFSET,
	},
	ref,
) {
	const supportsHover = useMedia("(hover: hover)");

	return (
		<Tooltip
			ref={ref}
			arrow={false}
			content={content}
			offset={offset}
			className={cx(styles.tooltip, className)}
			disabled={disabled || !supportsHover}
			onMount={onMount}
			placement={placement}
			triggerTarget={triggerTarget}
			visible={visible}
		>
			{children}
		</Tooltip>
	);
});

function ChapterPreviewTooltip({
	chapter,
	children,
}: {
	chapter: { title: string; timestamp: number; cue?: VTTCue | null } | null;
	children: React.ComponentProps<typeof ControlTooltip>["children"];
}) {
	const previewRef = React.useRef<MediaPreviewThumbnailElement | null>(null);
	const coords = React.useMemo(
		() => chapter?.cue && (getThumbnailImageCoords(chapter.cue.text)?.join(" ") ?? ""),
		[chapter],
	);

	// Call the update method on the MediaPreviewThumbnail whenever we show the
	// tooltip. Without this it won't update the styles to actually show the image.
	const onMount = React.useCallback(() => {
		previewRef.current?.update();
	}, []);

	const hours = chapter && Math.floor(chapter.timestamp / 3600);
	const minutes =
		chapter &&
		Math.floor((chapter.timestamp % 3600) / 60)
			.toString()
			.padStart(2, "0");
	const seconds =
		chapter &&
		Math.floor(chapter.timestamp % 60)
			.toString()
			.padStart(2, "0");

	return (
		<ControlTooltip
			onMount={onMount}
			className={styles.chapterPreviewTooltip}
			placement="top-start"
			content={
				!chapter ? null : (
					<div className={styles.chapterPreview}>
						{chapter.cue && (
							<MediaPreviewThumbnail
								ref={previewRef}
								mediaPreviewImage={chapter.cue.text}
								mediaPreviewCoords={coords}
								style={{ maxWidth: "96px", maxHeight: "54px", borderRadius: "6px" }}
								class={styles.chapterPreviewThumbnail}
							/>
						)}
						<div className={styles.chapterPreviewRight}>
							<p className={styles.chapterPreviewTitle}>{chapter.title}</p>
							<div>
								<span className={styles.chapterPreviewTimestamp}>
									{hours ? `${hours}:` : ""}
									{minutes}:{seconds}
								</span>
							</div>
						</div>
					</div>
				)
			}
			disabled={!chapter}
		>
			{children}
		</ControlTooltip>
	);
}

export const Video = React.forwardRef<HTMLVideoElement, VideoProps>(function Video(
	{
		children,
		isMiniPlayerActive,
		toggleMiniPlayer,
		thumbnailTrack,
		className,
		onTimeUpdate,
		inClippingMode,
		openClipTrimmer,
		numClips,
		isClip,
		setSelectedRecordingId,
		parts,
		hideClipButton,
		allowRecapDownload = true,
		meetingRecordingKey,
		isCollapsed,
		setIsCollapsed,
		...videoProps
	},
	ref,
) {
	const meetingRecording = useFragment(
		graphql`
			fragment Video_meetingRecording on MeetingRecordingInterface {
				__typename
				id
				createdAt
				isFavourite
				meetingRecordedVideoUrl
				transcriptTxtDownloadUrl
				meetingRecordedVideoPosterUrl
				viewCount
				mediaType

				... on MeetingRecording {
					meetingCallEnd
					aiArtifactsTxtDownloadUrl

					activeSummary {
						chapters {
							timestamp
							title
							bulletPoints {
								text
								timestamp
								textShort
								context
							}
						}
					}
					...Timeline_meetingKeyEvents
				}
			}
		`,
		meetingRecordingKey,
	);

	const recordingOrClipId = meetingRecording.id;
	const addedAt = meetingRecording.meetingCallEnd || meetingRecording.createdAt;

	const {
		isFavourite,
		transcriptTxtDownloadUrl,
		aiArtifactsTxtDownloadUrl,
		viewCount,
		meetingRecordedVideoUrl,
		meetingRecordedVideoPosterUrl,
		mediaType,
	} = meetingRecording;

	const { user: viewerUser } = React.useContext(AppContext);
	const videoRef = useForwardedRef<HTMLVideoElement>(ref);
	const controlsRef = React.useRef<VideoControlsRef>(null);
	const [isNarrow, setIsNarrow] = React.useState(false);
	const { ref: headerRef } = useResizeObserver<HTMLDivElement>({
		onResize: useMemo(
			() =>
				throttle(({ width }) => {
					if (!width) return;

					setIsNarrow(width < 385);
				}, 200),
			[],
		),
	});

	function onVideoTimeUpdate(event: React.SyntheticEvent<HTMLVideoElement>) {
		onTimeUpdate?.(event);
		controlsRef.current?.onVideoTimeUpdate(event);
	}

	function onAudioTimeUpdate(event: React.SyntheticEvent<HTMLAudioElement>) {
		// @ts-ignore
		onTimeUpdate?.(event);
	}

	const { analyticsFingerprint } = React.useContext(BillingContext);

	const handleDoubleClick = React.useCallback(() => {
		if (isMiniPlayerActive && toggleMiniPlayer) {
			toggleMiniPlayer();
		}
	}, [isMiniPlayerActive, toggleMiniPlayer]);

	const enterClipMode = React.useCallback(() => {
		openClipTrimmer?.();
		setIsCollapsed(false);
		// We want to exit fullscreen when entering clipping mode so that clipping controls are visible
		if (document.fullscreenElement) document.exitFullscreen();
		dispatchAnalyticsTrack("[AI Copilot] Started Clipping", { fingerprint: analyticsFingerprint });
	}, [analyticsFingerprint, openClipTrimmer, setIsCollapsed]);

	const onFavouriteClick = React.useCallback(() => {
		if (viewerUser?.isGuest) {
			return;
		}

		addOrRemoveRecordingOrClipFromFavourites({
			newIsFavourite: !isFavourite,
			recordingOrClipId,
			videoType: isClip ? "clip" : "recording",
		});
	}, [isClip, isFavourite, recordingOrClipId, viewerUser?.isGuest]);

	const onPartChange = React.useCallback(
		(val: SingleValue<SelectorOption>) => {
			if (!val || !setSelectedRecordingId) return;

			setSelectedRecordingId(val.value);
		},
		[setSelectedRecordingId],
	);

	const isPublic = useIsPublic();
	const onClickDownload = React.useCallback(
		(type: "video" | "transcript" | "artifacts") => {
			dispatchAnalyticsTrack("[AI Copilot] Download clicked", {
				fingerprint: analyticsFingerprint,
				source: isPublic ? "Public View" : "Modal",
				type,
			});
		},
		[analyticsFingerprint, isPublic],
	);

	const onClickDownloadVideo = React.useCallback(() => {
		onClickDownload("video");
	}, [onClickDownload]);

	const onClickDownloadTranscript = React.useCallback(() => {
		onClickDownload("transcript");
	}, [onClickDownload]);

	const onClickDownloadAiArtifacts = React.useCallback(() => {
		onClickDownload("artifacts");
	}, [onClickDownload]);

	const videoTitle = React.useMemo(() => {
		if (isNarrow) {
			return new Intl.DateTimeFormat("en-US", {
				day: "numeric",
				month: "short",
			}).format(new Date(addedAt));
		}

		const addedStr = formatDate(new Date(addedAt));

		return viewCount ? `Added ${addedStr}・${viewCount} view${viewCount === 1 ? "" : "s"}` : `Added ${addedStr}`;
	}, [isNarrow, addedAt, viewCount]);

	const isLibraryFeatureOn = useLibraryNavigationFeatureFlagOn();
	const solidWatchLaterIcon = isLibraryFeatureOn ? faClockSolid : faStarSolid;
	const lightWatchLaterIcon = isLibraryFeatureOn ? faClockLight : faStarLight;
	const [downloadMenuIsOpen, setDownloadMenuIsOpen] = React.useState(false);
	const onChangeDownloadMenuIsOpen = React.useCallback((value: boolean) => {
		setDownloadMenuIsOpen(value);
	}, []);

	const videoCollapsed = (isCollapsed && !isMiniPlayerActive) || mediaType === "AUDIO";
	const mediaFileExtension = React.useMemo(() => {
		// This value shouldn't be used if there's no file, but just in case we'll
		// fall back to something. mp4 is also a pretty safe bet regardless.
		if (!meetingRecordedVideoUrl) return "mp4";

		const url = new URL(meetingRecordedVideoUrl);
		const lastDotIndex = url.pathname.lastIndexOf(".");
		if (lastDotIndex === -1) return "mp4";
		return url.pathname.slice(lastDotIndex + 1) || "mp4";
	}, [meetingRecordedVideoUrl]);

	return (
		<CenteredControls videoRef={videoRef} inClippingMode={inClippingMode} collapsed={videoCollapsed}>
			{({ centeredControlsVisible }: CenteredControlsRenderProps) => (
				<MediaController
					autohide={videoCollapsed || inClippingMode ? "-1" : "2"}
					className={cx(styles.controller, {
						[styles.miniPlayer]: isMiniPlayerActive,
					})}
				>
					<MediaControlBar noautohide={videoCollapsed || inClippingMode} className={styles.backdropControlBar}>
						<div className={styles.playerBackdrop} />
					</MediaControlBar>

					{mediaType === "VIDEO" ? (
						<motion.video
							playsInline
							preload="metadata"
							width="100%"
							// @ts-ignore
							ref={videoRef}
							slot="media"
							className={cx(styles.video, className)}
							onTimeUpdate={onVideoTimeUpdate}
							onDoubleClick={handleDoubleClick}
							src={meetingRecordedVideoUrl ?? ""}
							poster={meetingRecordedVideoPosterUrl ?? undefined}
							{...videoProps}
							initial={false}
							animate={videoCollapsed ? { height: "103px", opacity: 0 } : { height: "auto", opacity: 1 }}
							transition={{ type: "spring", bounce: 0, duration: isMiniPlayerActive ? 0 : 0.5 }}
						>
							{children}
						</motion.video>
					) : (
						<audio
							src={meetingRecordedVideoUrl || ""}
							onTimeUpdate={onAudioTimeUpdate}
							playsInline
							preload="metadata"
							ref={videoRef}
							slot="media"
							{...videoProps}
						/>
					)}
					{!isMiniPlayerActive && (
						<MediaControlBar
							noautohide={videoCollapsed || inClippingMode}
							className={cx(styles.headerControlBar, "force-dark-theme")}
						>
							<div className={styles.headerBackground}>
								<div className={styles.headerControls} ref={headerRef}>
									<div className={styles.headerControlsBlock}>
										{!viewerUser?.isGuest && !isPublic && (
											<div className={styles.iconButton} onClick={onFavouriteClick}>
												<FontAwesomeIcon fixedWidth icon={isFavourite ? solidWatchLaterIcon : lightWatchLaterIcon} />
											</div>
										)}

										<p className={styles.title}>{videoTitle}</p>
									</div>

									<div className={styles.headerControlsBlock}>
										{parts && parts.length > 1 && (
											<div>
												<Selector
													condensed
													options={parts}
													value={parts.find(tab => tab.value === recordingOrClipId)}
													onChange={onPartChange}
													components={{ Menu: MenuWithForcedDarkTheme }}
												/>
											</div>
										)}

										{!isPublic && allowRecapDownload && (
											<Menu open={downloadMenuIsOpen} onOpenChange={onChangeDownloadMenuIsOpen}>
												<Menu.Trigger>
													<Button size="slim" className={styles.downloadButton} variant="secondary">
														Download &nbsp; <FontAwesomeIcon icon={downloadMenuIsOpen ? faChevronUp : faChevronDown} />
													</Button>
												</Menu.Trigger>
												<Menu.Content>
													<Tooltip
														content="Video is not available"
														zIndex={zindexRadixPopper + 1}
														disabled={!!meetingRecordedVideoUrl}
														placement="bottom"
													>
														{mediaType === "VIDEO" ? (
															<Menu.ItemLink
																href={meetingRecordedVideoUrl ?? undefined}
																rel="noreferrer"
																onClick={onClickDownloadVideo}
																disabled={!meetingRecordedVideoUrl}
																download
															>
																Video ({mediaFileExtension})
															</Menu.ItemLink>
														) : (
															<Menu.ItemLink
																href={meetingRecordedVideoUrl ?? undefined}
																rel="noreferrer"
																onClick={onClickDownloadVideo}
																disabled={!meetingRecordedVideoUrl}
																download
															>
																Audio ({mediaFileExtension})
															</Menu.ItemLink>
														)}
													</Tooltip>

													<Tooltip
														content="Transcript is not available"
														zIndex={zindexRadixPopper + 1}
														disabled={!!transcriptTxtDownloadUrl}
														placement="bottom"
													>
														<div>
															<Menu.ItemLink
																href={transcriptTxtDownloadUrl ?? undefined}
																download
																onSelect={onClickDownloadTranscript}
																disabled={!transcriptTxtDownloadUrl}
															>
																Transcript (txt)
															</Menu.ItemLink>
														</div>
													</Tooltip>

													<Tooltip
														content="AI Recap is not available"
														zIndex={zindexRadixPopper + 1}
														disabled={!!aiArtifactsTxtDownloadUrl}
														placement="bottom"
													>
														<div>
															<Menu.ItemLink
																href={aiArtifactsTxtDownloadUrl ?? undefined}
																download
																onSelect={onClickDownloadAiArtifacts}
																disabled={!aiArtifactsTxtDownloadUrl}
															>
																AI Recap (txt)
															</Menu.ItemLink>
														</div>
													</Tooltip>
												</Menu.Content>
											</Menu>
										)}
									</div>
								</div>
							</div>
						</MediaControlBar>
					)}
					{!centeredControlsVisible && (
						<VideoControls
							ref={controlsRef}
							videoRef={videoRef}
							toggleMiniPlayer={toggleMiniPlayer}
							thumbnailTrack={thumbnailTrack}
							chapters={meetingRecording.activeSummary?.chapters || []}
							isMiniPlayerActive={isMiniPlayerActive}
							enterClipMode={enterClipMode}
							keyEventsKey={meetingRecording}
							numClips={numClips}
							isClip={isClip}
							inClippingMode={inClippingMode}
							hideClipButton={hideClipButton}
							isAudio={mediaType === "AUDIO"}
							isCollapsed={isCollapsed}
						/>
					)}
				</MediaController>
			)}
		</CenteredControls>
	);
});

export interface Chapter {
	title: string;
	timestamp: number | null;
	bulletPoints?: readonly Readonly<BulletPoint>[];
}

interface VideoControlsProps {
	videoRef: React.RefObject<HTMLVideoElement>;
	toggleMiniPlayer: (() => void) | undefined;
	thumbnailTrack: TextTrack | null;
	chapters: readonly Readonly<{
		title: string;
		timestamp: number | null;
		bulletPoints?: readonly Readonly<BulletPoint>[];
	}>[];
	isMiniPlayerActive?: boolean;
	enterClipMode: () => void;
	keyEventsKey: (Timeline_meetingKeyEvents$key & { __typename: string }) | null;
	numClips: number;
	isClip: boolean;
	inClippingMode: boolean;
	hideClipButton?: boolean;
	isAudio: boolean;
	isCollapsed: boolean;
}

interface VideoControlsRef {
	onVideoTimeUpdate(event: React.SyntheticEvent<HTMLVideoElement>): void;
}

/**
 * Creates a detailed timeline by breaking down chapters based on overlapping bullet timestamps
 * Returns chronologically ordered slices that represent transitions between topics
 */
function createTimelineSlices(
	chapters: readonly Readonly<Chapter>[],
	videoDuration: number,
): { timestamp: number; title: string }[] {
	// Step 1: Collect all unique timestamps with their associated topics
	const timePoints: { timestamp: number; title: string }[] = [];

	// Add each chapter's start time
	chapters.forEach(chapter => {
		if (chapter.timestamp !== null) {
			timePoints.push({
				timestamp: chapter.timestamp,
				title: chapter.title,
			});
		}
		if (chapter.bulletPoints) {
			chapter.bulletPoints.forEach(bulletPoint => {
				if (bulletPoint.timestamp !== null) {
					timePoints.push({
						timestamp: bulletPoint.timestamp,
						title: chapter.title,
					});
				}
			});
		}
	});

	// Step 2: Sort by timestamp
	timePoints.sort((a, b) => a.timestamp - b.timestamp);

	// Step 3: Remove consecutive timepoints with the same title
	let uniqueTimePoints: typeof timePoints = [];
	for (let i = 0; i < timePoints.length; i++) {
		if (i === 0 || timePoints[i].title !== timePoints[i - 1].title) {
			uniqueTimePoints.push(timePoints[i]);
		}
	}

	// Step 4: Remove timepoints that are less than 1 minutes from the next one
	const TWO_MINUTES_IN_SECONDS = 120;
	const filteredTimePoints: typeof timePoints = [];
	for (let i = 0; i < uniqueTimePoints.length; i++) {
		const currentPoint = uniqueTimePoints[i];
		const nextPoint = uniqueTimePoints[i + 1];

		// Always keep the current point if it's the last one or if the next point is more than 2 minutes away
		if (!nextPoint || nextPoint.timestamp - currentPoint.timestamp >= TWO_MINUTES_IN_SECONDS) {
			filteredTimePoints.push(currentPoint);
		}
	}
	// Step 5: Remove consecutive timepoints again
	uniqueTimePoints = [];
	for (let i = 0; i < filteredTimePoints.length; i++) {
		if (i === 0 || filteredTimePoints[i].title !== filteredTimePoints[i - 1].title) {
			uniqueTimePoints.push(filteredTimePoints[i]);
		}
	}

	// Add start point if needed
	if (uniqueTimePoints.length === 0 || uniqueTimePoints[0].timestamp > 0) {
		uniqueTimePoints.unshift({ timestamp: 0, title: "Introduction" });
	}

	// Add end point if needed (using the last known topic)
	if (uniqueTimePoints.length > 0 && uniqueTimePoints[uniqueTimePoints.length - 1].timestamp < videoDuration) {
		const lastTopic = uniqueTimePoints[uniqueTimePoints.length - 1].title;
		uniqueTimePoints.push({ timestamp: videoDuration as number, title: lastTopic });
	}

	return uniqueTimePoints;
}

interface ClipButtonProps {
	onClipButtonClick: () => void;
}

interface BaseClipButtonProps extends ClipButtonProps {
	disabled?: boolean;
	children: React.ReactNode;
}

const BaseClipButton: React.FC<BaseClipButtonProps> = ({ onClipButtonClick, disabled, children }) => (
	<FellowVideoPlayerButton onClick={onClipButtonClick} disabled={disabled}>
		{children}
	</FellowVideoPlayerButton>
);

const ClipButtonIcon = React.forwardRef<HTMLSpanElement>(function ClipButtonIcon(props, ref) {
	// TODO: It would be better to get this from the viewer
	const uxSimplificationEnabled = document.documentElement.dataset.uxSimplification === "enabled";

	return (
		<span ref={ref} slot="icon" className={styles.clipIconContainer}>
			<FontAwesomeIcon icon={faScissors} />
			<span className={cx(styles.clipText, { [styles.hiddenWhenLittleSpace]: uxSimplificationEnabled })}>Clip</span>
		</span>
	);
});

interface PrivateClipButtonProps extends ClipButtonProps {
	inClippingMode: boolean;
	isAudio: boolean;
	tooManyClips: boolean;
	videoDuration: number;
}

const PrivateClipButton: React.FC<PrivateClipButtonProps> = ({
	onClipButtonClick,
	inClippingMode,
	isAudio,
	tooManyClips,
	videoDuration,
}) => {
	const disabled =
		isAudio ||
		tooManyClips ||
		isNaN(videoDuration) ||
		videoDuration <= MIN_VIDEO_DURATION_FOR_CLIPPING ||
		inClippingMode;

	const tooltipContent = isAudio
		? "Clipping for audio recordings coming soon"
		: tooManyClips
			? `You can't create more than ${MAX_CLIPS_PER_RECORDING} clips of a recording`
			: "This recording is too short to create a clip";

	const tooltipDisabled =
		!isAudio && !tooManyClips && (isNaN(videoDuration) || videoDuration > MIN_VIDEO_DURATION_FOR_CLIPPING);

	return (
		<BaseClipButton onClipButtonClick={onClipButtonClick} disabled={disabled}>
			<ControlTooltip content={tooltipContent} disabled={tooltipDisabled}>
				<ClipButtonIcon />
			</ControlTooltip>
		</BaseClipButton>
	);
};

const VideoControls = React.forwardRef<VideoControlsRef, VideoControlsProps>(function VideoControls(
	{
		videoRef,
		toggleMiniPlayer,
		thumbnailTrack,
		chapters: chaptersWithoutCue,
		isMiniPlayerActive,
		enterClipMode,
		keyEventsKey,
		numClips,
		isClip,
		inClippingMode,
		hideClipButton,
		isAudio,
		isCollapsed,
	},
	ref,
) {
	const narrowViewport = useIsNarrowViewport();
	const slices: readonly { timestamp: number; title: string; cue?: VTTCue | null }[] = React.useMemo(() => {
		if (!thumbnailTrack)
			return chaptersWithoutCue.filter(chapter => chapter.timestamp != null) as readonly {
				timestamp: number;
				title: string;
				bulletPoints?: readonly Readonly<BulletPoint>[];
			}[];
		const slicesWithCues = createTimelineSlices(chaptersWithoutCue, videoRef.current?.duration ?? NaN);
		return addTextTrackCuesToChapters<{ timestamp: number; title: string }>(
			thumbnailTrack,
			slicesWithCues,
			chapter => chapter.timestamp ?? 0,
		);
	}, [chaptersWithoutCue, thumbnailTrack, videoRef]);

	const onNextChapterButtonClick = React.useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		// Set the video's currentTime to the first chapter that comes after the
		// currentTime (i.e. the next chapter)
		for (const { timestamp } of slices) {
			if (timestamp > video.currentTime) {
				video.currentTime = timestamp;
				return;
			}
		}
	}, [slices, videoRef]);

	const getNextChapter = React.useCallback(
		(video: HTMLVideoElement) => {
			for (const chapter of slices) {
				if (chapter.timestamp > video.currentTime) {
					return chapter;
				}
			}
			return null;
		},
		[slices],
	);

	const [nextChapter, setNextChapter] = React.useState<(typeof slices)[number] | null>(null);

	// Reset the next chapter whenever the chapters list changes
	React.useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		setNextChapter(getNextChapter(video));
	}, [videoRef, getNextChapter]);

	React.useImperativeHandle(ref, () => ({
		onVideoTimeUpdate(event: React.SyntheticEvent<HTMLVideoElement>) {
			const video = event.currentTarget;
			if (!(video instanceof HTMLVideoElement)) return;
			setNextChapter(getNextChapter(video));
		},
	}));

	const onPlaybackRateChange = React.useCallback(
		(playbackRate: number) => {
			if (!videoRef?.current) return;
			videoRef.current.playbackRate = playbackRate;
		},
		[videoRef],
	);

	const isPublic = useIsPublic();

	const [videoDuration, setVideoDuration] = React.useState(videoRef.current?.duration ?? NaN);
	React.useEffect(() => {
		const { current: video } = videoRef;

		function onLoadedMetadata() {
			if (!video) return;
			setVideoDuration(video.duration);
		}

		video?.addEventListener("loadedmetadata", onLoadedMetadata);
		return () => {
			video?.removeEventListener("loadedmetadata", onLoadedMetadata);
		};
	}, [videoRef]);

	const onClipButtonClick = React.useCallback(() => enterClipMode(), [enterClipMode]);

	const tooManyClips = numClips >= MAX_CLIPS_PER_RECORDING;

	// TODO: It would be better to get this from the viewer
	const uxSimplificationEnabled = document.documentElement.dataset.uxSimplification === "enabled";

	return (
		<>
			{videoRef.current && !inClippingMode && (
				<MediaControlBar className={styles.timeRangeControlBar}>
					<div className={styles.timeDisplayContainer}>
						<MediaTimeDisplay noToggle className={styles.timeDisplay} />
						<MediaTimeDisplay remaining noToggle className={styles.timeDisplay} />
					</div>
					<Timeline keyEventsKey={keyEventsKey} videoRef={videoRef} chapters={slices} />
				</MediaControlBar>
			)}
			<MediaControlBar
				noautohide={isCollapsed}
				className={cx(styles.mainControlBar, { [styles.collapsed]: isCollapsed })}
			>
				<MediaPlayButton>
					<ControlTooltip content="Play">
						<span slot="play">
							<PlayIcon />
						</span>
					</ControlTooltip>
					<ControlTooltip content="Pause">
						<span slot="pause">
							<PauseIcon />
						</span>
					</ControlTooltip>
				</MediaPlayButton>
				{!!slices.length && (
					<FellowVideoPlayerButton
						onClick={onNextChapterButtonClick}
						disabled={!nextChapter || inClippingMode}
						className={cx({ [styles.hiddenWhenLittleSpace]: uxSimplificationEnabled })}
					>
						<ChapterPreviewTooltip chapter={nextChapter}>
							<span slot="icon">
								<NextChapterIcon />
							</span>
						</ChapterPreviewTooltip>
					</FellowVideoPlayerButton>
				)}
				{/* NOTE: The mute button must be immediately followed by the volume range */}
				<MediaMuteButton className={styles.muteButton}>
					<ControlTooltip content="Unmute">
						<span slot="off">
							<VolumeOffIcon />
						</span>
					</ControlTooltip>
					<ControlTooltip content="Mute">
						<span slot="low">
							<VolumeMediumIcon />
						</span>
					</ControlTooltip>
					<ControlTooltip content="Mute">
						<span slot="medium">
							<VolumeMediumIcon />
						</span>
					</ControlTooltip>
					<ControlTooltip content="Mute">
						<span slot="high">
							<VolumeHighIcon />
						</span>
					</ControlTooltip>
				</MediaMuteButton>
				<ControlTooltip content="Volume">
					<MediaVolumeRange
						className={cx(styles.volumeRange, { [styles.hiddenWhenLittleSpace]: uxSimplificationEnabled })}
					/>
				</ControlTooltip>

				<div className={styles.spacer} />
				<PlaybackRateSelector videoRef={videoRef} onPlaybackRateChange={onPlaybackRateChange} />
				{!hideClipButton && !isMiniPlayerActive && !isClip && !isPublic && (
					<PrivateClipButton
						onClipButtonClick={onClipButtonClick}
						inClippingMode={inClippingMode}
						isAudio={isAudio}
						tooManyClips={tooManyClips}
						videoDuration={videoDuration}
					/>
				)}
				{!isAudio && (
					<MediaCaptionsButton disabled={isCollapsed}>
						<ControlTooltip content="Turn on captions">
							<span slot="off">
								<CaptionsOffIcon />
							</span>
						</ControlTooltip>
						<ControlTooltip content="Turn off captions">
							<span slot="on">
								<CaptionsOnIcon />
							</span>
						</ControlTooltip>
					</MediaCaptionsButton>
				)}
				{!uxSimplificationEnabled && !isPublic && !narrowViewport && !isMiniPlayerActive && toggleMiniPlayer && (
					<FellowVideoPlayerButton onClick={toggleMiniPlayer} disabled={inClippingMode}>
						<ControlTooltip content="Miniplayer">
							<span slot="icon">
								<MiniplayerIcon />
							</span>
						</ControlTooltip>
					</FellowVideoPlayerButton>
				)}

				<MediaFullscreenButton disabled={inClippingMode}>
					<ControlTooltip content="Fullscreen">
						<span slot="enter">
							<FullscreenEnterIcon />
						</span>
					</ControlTooltip>
					<ControlTooltip content="Exit fullscreen">
						<span slot="exit">
							<FullscreenExitIcon />
						</span>
					</ControlTooltip>
				</MediaFullscreenButton>
			</MediaControlBar>
		</>
	);
});
